/* eslint-disable max-lines */
import type { InfiniteData, QueryKey } from '@tanstack/react-query';
import { CancelledError } from '@tanstack/react-query';

import { createCacheKeyBuilder } from './createPaginationEntityCacheHelpers';
import { getQueryClient } from '../queryClientSingleton';
import {
    getInfiniteQueryFn,
    getNextPageParam,
    getQueryFn,
} from './createPaginationEntityQueryClientHelpers';
import type {
    CustomQueryPageParams,
    QueryPage,
    QueryPageReturnType,
    QueryParamsBaseType,
} from './createPaginationEntityTypes';
import type { CacheKey } from '../utils/internalTypings';
import { createRetryHandler } from '../utils/retryConfigs';
import { setQueryDataWithDefaults } from '../utils/setQueryDataWithDefaults';

export interface RefetchResult<Entity> {
    data?: Entity[];
    hasNextPage: boolean;
    fetchMore(options?: { signal?: AbortSignal }): Promise<RefetchResult<Entity>>;
}

function getLastPage<T>(infiniteData: InfiniteData<T> | undefined): T | undefined {
    if (!infiniteData?.pages?.length) {
        return undefined;
    }

    return infiniteData.pages[infiniteData.pages.length - 1];
}

function getNextPageInfiniteData<InternalPage, Params>({
    previousData = { pages: [], pageParams: [] },
    params,
    page,
}: {
    previousData?: InfiniteData<InternalPage>;
    params: Params;
    page: InternalPage;
}): InfiniteData<InternalPage> {
    const { pages, pageParams } = previousData;

    return {
        pages: [...pages, page],
        pageParams: [...pageParams, params],
    };
}

// fetch more in react-query is only available as a result in react-query's hook
// it's state only lives inside infinite query observer, which is instantiated for each infinite query hook mount
// so we will have to re-create the functionality ourselves

/**
 * Factory function which creates recursive fetch more function
 */
function createFetchMore<QueryParams extends QueryParamsBaseType, Entity>(
    {
        queryKey,
        queryPage,
        name,
        selectId,
        skipEntityCacheSync,
    }: {
        queryKey: QueryKey;
        queryPage: QueryPage<Entity, QueryParams, unknown>;
        name: string;
        selectId: CacheKey<Entity>;
        skipEntityCacheSync: boolean;
    },
    client = getQueryClient(),
): () => Promise<RefetchResult<Entity>> {
    const fetchMore = async ({ signal }: { signal?: AbortSignal } = {}): Promise<
        RefetchResult<Entity>
    > => {
        // the state between returning fetchMore function and calling it can actually change,
        // so we have to always check the state runtime during the call
        const data = client.getQueryData(queryKey) as
            | InfiniteData<QueryPageReturnType<Entity>>
            | undefined;

        const lastPage = getLastPage(data);
        const params = lastPage && getNextPageParam<QueryParams, Entity>(lastPage);

        if (!params) {
            return {
                data: data?.pages?.flatMap((p) => p.data),
                hasNextPage: false,
                fetchMore,
            };
        }

        const infiniteQuery = getInfiniteQueryFn({
            queryPage,
            params,
            name,
            selectId,
            cancelable: true,
            skipEntityCacheSync,
        });

        try {
            const page = await infiniteQuery({ pageParam: params, signal });
            const queryData = getNextPageInfiniteData({ previousData: data, page, params });

            setQueryDataWithDefaults(client, queryKey, () => {
                return queryData;
            });

            const nextPageParams = getNextPageParam(page);

            return {
                fetchMore,
                data: queryData.pages.flatMap((p) => p.data),
                hasNextPage: !!nextPageParams,
            };
        } catch (e) {
            if (signal?.aborted) {
                throw new CancelledError();
            }

            throw e;
        }
    };

    return fetchMore;
}

export async function refetchEntities<Entity, QueryParams extends QueryParamsBaseType>(
    {
        params,
        queryPage,
        name,
        signal,
        selectId,
        skipParamsHashing,
        skipEntityCacheSync,
    }: {
        params: QueryParams;
        queryPage: QueryPage<Entity, QueryParams, unknown>;
        name: string;
        skipParamsHashing?: boolean;
        skipEntityCacheSync: boolean;
        signal?: AbortSignal;
        selectId: CacheKey<Entity>;
    },
    client = getQueryClient(),
): Promise<RefetchResult<Entity>> {
    const queryKey = createCacheKeyBuilder(name).list(skipParamsHashing ? {} : params);

    const infiniteQueryFn = getInfiniteQueryFn({
        queryPage,
        name,
        params,
        selectId,
        cancelable: !!signal,
        skipEntityCacheSync,
    });

    const onAbort = (): void => {
        client.cancelQueries({ queryKey, exact: true });

        signal?.removeEventListener('abort', onAbort);
    };

    signal?.addEventListener('abort', onAbort);

    const data = await client.fetchInfiniteQuery({
        initialPageParam: {},
        queryKey,
        queryFn: infiniteQueryFn,
        retry: createRetryHandler(!!signal),
        gcTime: Infinity,
        getNextPageParam,
    });

    const fetchMore = createFetchMore(
        {
            queryKey,
            name,
            queryPage: (pageParam, fetchMoreSignal) =>
                infiniteQueryFn({
                    pageParam: pageParam as CustomQueryPageParams<QueryParams>,
                    signal: fetchMoreSignal.signal,
                }),
            selectId,
            skipEntityCacheSync,
        },
        client,
    );

    const page = getLastPage(data);
    const nextParams = page && getNextPageParam(page);

    return {
        fetchMore,
        data: data.pages?.flatMap((p) => p.data),
        hasNextPage: !!nextParams,
    };
}

export async function queryPaginationEntitiesIfNeeded<
    Entity,
    QueryParams extends QueryParamsBaseType,
>(
    {
        params,
        queryPage,
        name,
        signal,
        selectId,
        skipParamsHashing,
        skipEntityCacheSync,
    }: {
        params: QueryParams;
        queryPage: QueryPage<Entity, QueryParams, unknown>;
        name: string;
        skipParamsHashing?: boolean;
        skipEntityCacheSync: boolean;
        signal?: AbortSignal;
        selectId: CacheKey<Entity>;
    },
    client = getQueryClient(),
): Promise<RefetchResult<Entity>> {
    const state = client.getQueryState<Entity>(
        createCacheKeyBuilder(name).list(skipParamsHashing ? {} : params),
    );

    const data = state?.data as InfiniteData<QueryPageReturnType<Entity>> | undefined;

    if (data?.pages && state?.fetchStatus !== 'fetching') {
        const page = getLastPage(data);
        const nextParams = page && getNextPageParam(page);

        return {
            hasNextPage: !!nextParams,
            data: data.pages.flatMap((p) => p.data),
            fetchMore: createFetchMore(
                {
                    queryKey: createCacheKeyBuilder(name).list(skipParamsHashing ? {} : params),
                    queryPage,
                    name,
                    selectId,
                    skipEntityCacheSync,
                },
                client,
            ),
        };
    }

    return refetchEntities(
        { name, queryPage, params, signal, selectId, skipEntityCacheSync },
        client,
    );
}

export async function refetchPaginationEntity<Entity, QueryByIdParams extends QueryParamsBaseType>(
    {
        id,
        fetch,
        params,
        name,
        signal,
        localListsSynchronisation,
        selectId,
        skipEntityCacheSync,
    }: {
        id: string;
        params?: QueryByIdParams;
        fetch: (id: string, params?: QueryByIdParams) => Promise<Entity>;
        name: string;
        signal?: AbortSignal;
        localListsSynchronisation: boolean | undefined;
        selectId: CacheKey<Entity>;
        skipEntityCacheSync: boolean;
    },
    client = getQueryClient(),
): Promise<Entity> {
    const queryKey = createCacheKeyBuilder(name).detail(id);

    // eslint-disable-next-line sonarjs/no-identical-functions
    const onAbort = (): void => {
        client.cancelQueries({ queryKey, exact: true });

        signal?.removeEventListener('abort', onAbort);
    };

    signal?.addEventListener('abort', onAbort);

    return client.fetchQuery({
        queryKey: createCacheKeyBuilder(name).detail(id),
        queryFn: getQueryFn({
            id,
            params,
            queryById: fetch,
            name,
            cancelable: !!signal,
            selectId,
            localListsSynchronisation,
            skipEntityCacheSync,
        }),
        retry: createRetryHandler(!!signal),
        gcTime: Infinity,
    });
}

export async function queryPaginationEntityIfNeeded<
    Entity,
    QueryByIdParams extends QueryParamsBaseType,
>(
    {
        id,
        fetch,
        name,
        params,
        signal,
        selectId,
        localListsSynchronisation,
        skipEntityCacheSync,
    }: {
        id: string;
        params?: QueryByIdParams;
        fetch: (id: string, params?: QueryByIdParams) => Promise<Entity>;
        name: string;
        signal?: AbortSignal;
        localListsSynchronisation: boolean | undefined;
        selectId: CacheKey<Entity>;
        skipEntityCacheSync: boolean;
    },
    client = getQueryClient(),
): Promise<Entity> {
    const entityState = client.getQueryState<Entity>(createCacheKeyBuilder(name).detail(id));

    if (entityState?.data && entityState?.fetchStatus !== 'fetching') {
        return entityState.data;
    }

    return refetchPaginationEntity(
        {
            id,
            name,
            fetch,
            params,
            signal,
            selectId,
            localListsSynchronisation,
            skipEntityCacheSync,
        },
        client,
    );
}
